import * as React from "react";
import { abbrData, basicData } from "./data";
import type { LocalizedOrString, PublicationItem, TalkItem } from "../types/data";

export const yourNameEn = basicData.name.firstName.en + " " + basicData.name.familyName.en;
export const yourNameJa = basicData.name.familyName.ja + " " + basicData.name.firstName.ja;
export const yourNameAbbr =
  basicData.name.firstName.en[0] + "." + basicData.name.familyName.en[0] + ".";

export const getLocalizedText = (value: LocalizedOrString | undefined, langEn: boolean): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  const primary = langEn ? value.en : value.ja;
  const fallback = langEn ? value.ja : value.en;
  return (primary ?? fallback ?? "").trim();
};

const getAbbrExpansion = (abbr: string | undefined, langEn: boolean): string => {
  if (!abbr) return "";
  const entry = abbrData?.abbr?.[abbr];
  if (!entry) return "";
  return entry;
};

export const getPublicationVenue = (item: PublicationItem, langEn: boolean): string => {
  const book = item.book ? getLocalizedText(item.book, langEn) : "";
  const conf = item.conf ? getLocalizedText(item.conf, langEn) : "";
  return (book || conf || getAbbrExpansion(item.abbr, langEn)).trim();
};

export const getTalkVenue = (item: TalkItem, langEn: boolean): string => {
  const conf = getLocalizedText(item.conf, langEn);
  const place = getLocalizedText(item.place, langEn);
  const confWithAbbr = item.abbr && conf ? `${conf} (${item.abbr})` : conf;
  if (confWithAbbr && place) return `${confWithAbbr}, ${place}`;
  return (confWithAbbr || place).trim();
};

const sanitizeBibtexValue = (value: string): string =>
  value.replace(/[{}]/g, "").replace(/\s+/g, " ").trim();

const wrapBibtexValue = (value: string): string => `{${sanitizeBibtexValue(value)}}`;

const toBibtexAuthorName = (input: string): string => {
  if (!input) return "";
  const parts = input.trim().replace(/\s+/g, " ").split(" ").filter(Boolean);
  if (parts.length <= 1) return parts.join("");

  const suffixSet = new Set(["jr", "sr", "ii", "iii", "iv", "v"]);
  const lastLower = parts[parts.length - 1].toLowerCase().replace(/\.$/, "");
  const suffix = suffixSet.has(lastLower) ? parts.pop()! : "";

  const lastName = parts.pop()!;
  const givenNames = parts.join(" ");
  const base = `${lastName}, ${givenNames}`.trim().replace(/,\s*$/, "");
  return suffix ? `${base}, ${suffix.replace(/\.$/, "")}.` : base;
};

const toBibtexAuthors = (authors: string): string =>
  authors
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean)
    .map(toBibtexAuthorName)
    .join(" and ");

const buildBibtexKey = (item: PublicationItem, langEn: boolean): string => {
  const authors = getLocalizedText(item.authors, langEn);
  const firstAuthor = authors.split(",")[0]?.trim() ?? "unknown";
  const lastName = firstAuthor.split(" ").filter(Boolean).pop() ?? "unknown";
  const year = item.year ? String(item.year) : "nd";
  const title = getLocalizedText(item.title, langEn);
  const firstWord = title.split(/\s+/).filter(Boolean)[0] ?? "work";
  const key = `${lastName}${year}${firstWord}`;
  return key.replace(/[^a-zA-Z0-9]+/g, "").toLowerCase();
};

const getBibtexType = (kind?: string): string => {
  switch (kind) {
    case "journal":
      return "article";
    case "reviewedProceeding":
    case "reviewedProceedingMinor":
    case "reviewedWorkshop":
      return "inproceedings";
    case "preprint":
      return "misc";
    case "book":
      return "book";
    case "dissertation":
      return "phdthesis";
    default:
      return "misc";
  }
};

const buildBibtexEntry = (item: PublicationItem, langEn: boolean): string => {
  const fields: Array<[string, string]> = [];

  const title = getLocalizedText(item.title, langEn);
  const authors = getLocalizedText(item.authors, langEn);
  const book = item.book ? getLocalizedText(item.book, langEn) : "";
  const conf = item.conf ? getLocalizedText(item.conf, langEn) : "";
  const venue = (book || conf || getAbbrExpansion(item.abbr, langEn)).trim();

  if (title) fields.push(["title", wrapBibtexValue(title)]);
  if (authors) {
    fields.push(["author", wrapBibtexValue(toBibtexAuthors(authors))]);
  }
  if (item.year) fields.push(["year", wrapBibtexValue(String(item.year))]);
  if (item.month) {
    fields.push(["month", wrapBibtexValue(getMonthName(item.month).toLowerCase())]);
  }
  if (item.pages) fields.push(["pages", wrapBibtexValue(item.pages)]);

  const kind = item.kind;
  if (kind === "journal") {
    if (venue) fields.push(["journal", wrapBibtexValue(venue)]);
  } else if (kind === "book") {
    if (venue) fields.push(["publisher", wrapBibtexValue(venue)]);
  } else if (kind === "dissertation") {
    if (venue) fields.push(["school", wrapBibtexValue(venue)]);
  } else {
    if (venue) fields.push(["booktitle", wrapBibtexValue(venue)]);
  }

  if (item.doi) {
    fields.push(["doi", wrapBibtexValue(item.doi)]);
  }
  if (item.paperUrl) {
    fields.push(["url", wrapBibtexValue(item.paperUrl)]);
  }

  if (fields.length === 0) return "";
  const key = buildBibtexKey(item, langEn);
  const entryType = getBibtexType(item.kind);
  const formattedFields = fields.map(([keyName, value]) => `  ${keyName} = ${value}`).join(",\n");

  return `@${entryType}{${key},\n${formattedFields}\n}`;
};

export const getBibtex = (item: PublicationItem, langEn: boolean): string => {
  if (typeof item.bibtex === "string" && item.bibtex.trim()) {
    return item.bibtex.trim();
  }
  return buildBibtexEntry(item, langEn);
};

export const getMonthName = (month: number): string => {
  switch (month) {
    case 1:
      return "Jan";
    case 2:
      return "Feb";
    case 3:
      return "Mar";
    case 4:
      return "Apr";
    case 5:
      return "May";
    case 6:
      return "Jun";
    case 7:
      return "Jul";
    case 8:
      return "Aug";
    case 9:
      return "Sep";
    case 10:
      return "Oct";
    case 11:
      return "Nov";
    case 12:
      return "Dec";
    default:
      return "";
  }
};

export const getMonthYearName = (month: number, year: number, langEn: boolean): string => {
  if (langEn) {
    return getMonthName(month) + ". " + String(year);
  } else {
    return String(year) + "年" + String(month) + "月";
  }
};

export const getAuthorElement = (authors_str: string, usebib: boolean = false) => {
  const authors = authors_str.split(",");
  const authorNum = authors.length;

  return authors.map((name, j) => {
    let seperator = ", ";
    if (j == authorNum - 1) {
      seperator = usebib ? "" : ".";
    } else if (authorNum >= 2 && j == authorNum - 2) {
      seperator = " & ";
    }
    return name.trim() == yourNameEn || name.trim() == yourNameJa ? (
      <span key={j}>
        <u>{usebib ? toBibliographyName(name) : name}</u>
        {seperator}
      </span>
    ) : (
      <span key={j}>
        {usebib ? toBibliographyName(name) : name}
        {seperator}
      </span>
    );
  });
};

export function toBibliographyName(input: string): string {
  if (!input) return "";

  const parts = input.trim().replace(/\s+/g, " ").split(" ").filter(Boolean);

  if (parts.length === 1) {
    return parts[0];
  }

  const suffixSet = new Set(["jr", "sr", "ii", "iii", "iv", "v"]);
  let suffix: string | null = null;
  const lastLower = parts[parts.length - 1].toLowerCase().replace(/\.$/, "");
  if (suffixSet.has(lastLower)) {
    suffix = parts.pop()!;
  }

  const lastName = parts.pop()!;
  const givenNames = parts;
  const initials = givenNames
    .map((gn) =>
      gn
        .split("-")
        .filter(Boolean)
        .map((seg) => seg[0]?.toUpperCase() ?? "")
        .map((ch) => `${ch}.`)
        .join(" ")
    )
    .filter(Boolean)
    .join(" ");

  const core = `${lastName}, ${initials || ""}`.trim().replace(/,\s*$/, "");
  return suffix ? `${core}, ${suffix.replace(/\.$/, "")}.` : core;
}
